import api from './api';

/**
 * HOME+ API service — family management endpoints for residents.
 * All routes are under /home/* and scoped to the authenticated user's condo/unit.
 */
export const homeApi = {
  // FIX (2026-08-31, Jaime bug): every list/create/update call now carries the
  // active unit id explicitly (query param for GET, unitId in body for POST/PUT)
  // instead of relying on the backend auto-resolver, which mis-resolves users
  // who both own a standalone unit and belong to a condo unit (e.g. Casa
  // Tajalí vs TH1). requireUnit on the backend gives explicit unitId priority.
  tasks: {
    // FEATURE (2026-09-01): opts.archived=true returns the archived history
    // (completed tasks auto-archive server-side 24h after completed_at).
    list: (unitId?: string, opts?: { archived?: boolean }) =>
      api.get('/home/tasks', { params: { ...(unitId ? { unitId } : {}), ...(opts?.archived ? { archived: 'true' } : {}) } }),
    create: (data: any, unitId?: string) => api.post('/home/tasks', { ...data, ...(unitId ? { unitId } : {}) }),
    update: (id: string, data: any, unitId?: string) => api.put(`/home/tasks/${id}`, { ...data, ...(unitId ? { unitId } : {}) }),
    // FEATURE (2026-09-01, Jaime ruling): series delete scope — the frontend
    // passes scope=single|series as a query param; backend decides which rows
    // to remove (this occurrence vs the whole series).
    delete: (id: string, opts?: { scope?: 'single' | 'series'; unitId?: string }) =>
      api.delete(`/home/tasks/${id}`, { params: { ...(opts?.scope ? { scope: opts.scope } : {}), ...(opts?.unitId ? { unitId: opts.unitId } : {}) } }),
  },
  shopping: {
    list: (unitId?: string) => api.get('/home/shopping', { params: { ...(unitId ? { unitId } : {}) } }),
    // FIX (2026-08-31): typeahead suggestions ("don't retype products")
    suggestions: (q: string, unitId?: string) => api.get('/home/shopping/suggestions', { params: { q, ...(unitId ? { unitId } : {}) } }),
    create: (data: any, unitId?: string) => api.post('/home/shopping', { ...data, ...(unitId ? { unitId } : {}) }),
    update: (id: string, data: any, unitId?: string) => api.put(`/home/shopping/${id}`, { ...data, ...(unitId ? { unitId } : {}) }),
    // FIX (2026-09-28, ticket #216): DELETE must carry the active unitId like
    // every other shopping call — requireUnit 400s ("unitId is required") for
    // multi-unit owners when it can't auto-resolve the unit.
    delete: (id: string, unitId?: string) => api.delete(`/home/shopping/${id}`, { params: { ...(unitId ? { unitId } : {}) } }),
  },
  pets: {
    list: (unitId?: string) => api.get('/home/pets', { params: { ...(unitId ? { unitId } : {}) } }),
    create: (data: any, unitId?: string) => api.post('/home/pets', { ...data, ...(unitId ? { unitId } : {}) }),
    update: (id: string, data: any, unitId?: string) => api.put(`/home/pets/${id}`, { ...data, ...(unitId ? { unitId } : {}) }),
    // FIX (2026-09-28, ticket #216): same missing-unitId pattern as shopping delete.
    delete: (id: string, unitId?: string) => api.delete(`/home/pets/${id}`, { params: { ...(unitId ? { unitId } : {}) } }),
  },
  // FEATURE (2026-09-22, Jaime scope correction): the standalone Notas section
  // was removed — notes now live per-task inside Tareas (note_content /
  // noteImage on the task PUT). homeApi.notes is down to `list` only, kept
  // solely for PlanLimitModal's usage fetcher (403 PLAN_LIMIT count).
  notes: {
    list: (unitId?: string) => api.get('/home/notes', { params: { ...(unitId ? { unitId } : {}) } }),
  },
  vehicles: {
    list: (unitId?: string) => api.get('/home/vehicles', { params: { ...(unitId ? { unitId } : {}) } }),
    create: (data: any, unitId?: string) => api.post('/home/vehicles', { ...data, ...(unitId ? { unitId } : {}) }),
    update: (id: string, data: any, unitId?: string) => api.put(`/home/vehicles/${id}`, { ...data, ...(unitId ? { unitId } : {}) }),
    delete: (id: string) => api.delete(`/home/vehicles/${id}`),
  },
  budget: {
    list: (unitId?: string) => api.get('/home/budget', { params: { ...(unitId ? { unitId } : {}) } }),
    create: (data: any, unitId?: string) => api.post('/home/budget', { ...data, ...(unitId ? { unitId } : {}) }),
    update: (id: string, data: any, unitId?: string) => api.put(`/home/budget/${id}`, { ...data, ...(unitId ? { unitId } : {}) }),
    // FIX (2026-09-28, ticket #216): same missing-unitId pattern as shopping delete.
    delete: (id: string, unitId?: string) => api.delete(`/home/budget/${id}`, { params: { ...(unitId ? { unitId } : {}) } }),
  },
  // F140/F141/F142: Equipos y Mantenimiento (unit equipment + maintenance log + reminders).
  // Built against the spec contract (spec-draft-equipos-mantenimiento-2026-09-17.md);
  // backend /api/home/equipment routes are being implemented separately.
  equipment: {
    list: (unitId?: string) => api.get('/home/equipment', { params: { ...(unitId ? { unitId } : {}) } }),
    get: (id: string, unitId?: string) => api.get(`/home/equipment/${id}`, { params: { ...(unitId ? { unitId } : {}) } }),
    create: (data: any, unitId?: string) => api.post('/home/equipment', { ...data, ...(unitId ? { unitId } : {}) }),
    update: (id: string, data: any, unitId?: string) => api.put(`/home/equipment/${id}`, { ...data, ...(unitId ? { unitId } : {}) }),
    delete: (id: string, unitId?: string) => api.delete(`/home/equipment/${id}`, { params: { ...(unitId ? { unitId } : {}) } }),
    // Per-equipment maintenance log (F141). registerInBudget: optional link to the
    // unit's Presupuesto (F141 ruling 17:21) — backend creates a home_budget_entries
    // row (category mantenimiento) when cost > 0 and the toggle is on.
    services: {
      list: (equipmentId: string, opts?: { page?: number; pageSize?: number; q?: string }) =>
        api.get(`/home/equipment/${equipmentId}/services`, { params: { ...(opts?.page ? { page: opts.page } : {}), ...(opts?.pageSize ? { pageSize: opts.pageSize } : {}), ...(opts?.q ? { q: opts.q } : {}) } }),
      create: (equipmentId: string, data: any, unitId?: string) => api.post(`/home/equipment/${equipmentId}/services`, { ...data, ...(unitId ? { unitId } : {}) }),
      update: (serviceId: string, data: any) => api.put(`/home/equipment-services/${serviceId}`, data),
      delete: (serviceId: string) => api.delete(`/home/equipment-services/${serviceId}`),
    },
    // F142: reminders optional per equipment; next-service default period (3, months) with
    // value + unit selectable (days/weeks/months/years); warranty-expiry lead default 14 days.
    reminders: {
      list: (unitId?: string, status?: string) => api.get('/home/equipment/reminders', { params: { ...(unitId ? { unitId } : {}), ...(status ? { status } : {}) } }),
      create: (equipmentId: string, data: any, unitId?: string) => api.post(`/home/equipment/${equipmentId}/reminders`, { ...data, ...(unitId ? { unitId } : {}) }),
      update: (reminderId: string, data: any) => api.put(`/home/equipment-reminders/${reminderId}`, data),
    },
  },
  // F143/F144/F145: Equipos del condominio (areas comunes). Registry scoped by
  // condo_id; services log with optional "Registrar en Gastos" (registerInExpense
  // -> backend creates an expenses row, category reparaciones, returns expenseId);
  // reminders condo-scoped (F145). Roles: admin/directivo edit, delete admin-only,
  // mantenimiento read + log entries, residents read-only.
  condoEquipment: {
    list: (condoId: string) => api.get(`/condo/equipment/condo/${condoId}`),
    get: (id: string) => api.get(`/condo/equipment/${id}`),
    create: (condoId: string, data: any) => api.post(`/condo/equipment/condo/${condoId}`, data),
    update: (id: string, data: any) => api.put(`/condo/equipment/${id}`, data),
    delete: (id: string) => api.delete(`/condo/equipment/${id}`),
    services: {
      list: (equipmentId: string) => api.get(`/condo/equipment/${equipmentId}/services`),
      create: (equipmentId: string, data: any) => api.post(`/condo/equipment/${equipmentId}/services`, data),
      update: (serviceId: string, data: any) => api.put(`/condo/equipment-services/${serviceId}`, data),
      delete: (serviceId: string) => api.delete(`/condo/equipment-services/${serviceId}`),
    },
    reminders: {
      list: (condoId: string, status?: string) => api.get(`/condo/equipment/reminders/condo/${condoId}`, { params: { ...(status ? { status } : {}) } }),
      create: (equipmentId: string, data: any) => api.post(`/condo/equipment/${equipmentId}/reminders`, data),
      update: (reminderId: string, data: any) => api.put(`/condo/equipment-reminders/${reminderId}`, data),
    },
  },
  // F103: Family members management
  family: {
    list: (condoId: string) => api.get(`/condos/${condoId}/family`),
    create: (condoId: string, data: { name: string; relationship: string; phone?: string; email?: string; birth_date?: string }) =>
      api.post(`/condos/${condoId}/family`, data),
    update: (condoId: string, memberId: string, data: any) => api.put(`/condos/${condoId}/family/${memberId}`, data),
    delete: (condoId: string, memberId: string) => api.delete(`/condos/${condoId}/family/${memberId}`),
  },
};

export default homeApi;
